<script>
import moment from "moment";

import Layout from "../../layouts/main";
import appConfig from "@/app.config";
import { leerBitacora } from "@/services/bitacora";

const VARIANTES = {
  crear: "success",
  editar: "primary",
  eliminar: "danger",
  activar: "info",
  desactivar: "warning",
};

export default {
  page: {
    title: "Bitácora",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: { Layout },
  data() {
    return {
      movimientos: [],
      cargando: true,
      error: null,
      busqueda: "",
      filtroAccion: "todas",
      currentPage: 1,
      perPage: 20,
      detalle: null,
      mostrarDetalle: false,
    };
  },
  computed: {
    acciones() {
      const set = new Set(this.movimientos.map((m) => m.accion).filter(Boolean));
      return [
        { value: "todas", text: "Todas las acciones" },
        ...Array.from(set).sort().map((a) => ({ value: a, text: capitalizar(a) })),
      ];
    },
    filas() {
      const q = this.busqueda.trim().toLowerCase();
      return this.movimientos
        .filter((m) => this.filtroAccion === "todas" || m.accion === this.filtroAccion)
        .filter((m) => {
          if (!q) return true;
          return [m.actor, m.coleccion, m.doc_id, m.descripcion]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q));
        })
        .map((m) => ({
          ...m,
          accionTexto: capitalizar(m.accion || "—"),
          variante: VARIANTES[m.accion] || "secondary",
          fecha: m.timestamp && m.timestamp.toDate
            ? moment(m.timestamp.toDate()).format("DD/MM/YYYY HH:mm")
            : "—",
        }));
    },
  },
  methods: {
    verDetalle(fila) {
      this.detalle = fila;
      this.mostrarDetalle = true;
    },
    formatearJson(obj) {
      return obj ? JSON.stringify(obj, null, 2) : "—";
    },
    async cargar() {
      this.cargando = true;
      this.error = null;
      try {
        this.movimientos = await leerBitacora(300);
      } catch (err) {
        console.error("[bitacora] error al leer:", err);
        this.error =
          "No se pudo leer la bitácora. Si es la primera vez, la colección aún no existe.";
      } finally {
        this.cargando = false;
      }
    },
  },
  mounted() {
    this.cargar();
  },
};

function capitalizar(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
</script>

<template>
  <Layout>
    <div class="card">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center flex-wrap mb-3">
          <div>
            <h4 class="card-title mb-1">Bitácora de acciones</h4>
            <p class="text-muted small mb-0">Quién creó, editó o eliminó cada registro</p>
          </div>
          <b-button size="sm" variant="outline-secondary" :disabled="cargando" @click="cargar">
            <i class="mdi mdi-refresh"></i> Actualizar
          </b-button>
        </div>

        <b-alert :show="!!error" variant="warning">{{ error }}</b-alert>

        <b-row class="mb-3">
          <b-col md="6" class="mb-2">
            <b-form-input v-model="busqueda" type="search"
              placeholder="Buscar por usuario, colección o documento…" />
          </b-col>
          <b-col md="3" class="mb-2">
            <b-form-select v-model="filtroAccion" :options="acciones" />
          </b-col>
        </b-row>

        <b-table responsive hover :items="filas" :per-page="perPage" :current-page="currentPage" :fields="[
          { key: 'fecha', label: 'Fecha' },
          { key: 'actor', label: 'Usuario' },
          { key: 'accionTexto', label: 'Acción' },
          { key: 'coleccion', label: 'Colección' },
          { key: 'descripcion', label: 'Detalle' },
          { key: 'acciones', label: '' },
        ]" show-empty :empty-text="cargando ? 'Cargando…' : 'Todavía no hay movimientos registrados.'">
          <template #cell(accionTexto)="{ item }">
            <b-badge :variant="item.variante">{{ item.accionTexto }}</b-badge>
          </template>
          <template #cell(descripcion)="{ item }">
            {{ item.descripcion || item.doc_id || '—' }}
          </template>
          <template #cell(acciones)="{ item }">
            <b-button size="sm" variant="outline-primary" @click="verDetalle(item)">
              <i class="mdi mdi-eye"></i>
            </b-button>
          </template>
        </b-table>

        <b-pagination v-model="currentPage" :total-rows="filas.length" :per-page="perPage" align="right" size="sm"
          class="mt-3" first-number last-number />
      </div>
    </div>

    <b-modal v-model="mostrarDetalle" size="lg" centered hide-footer title="Detalle del movimiento">
      <div v-if="detalle">
        <p class="mb-1"><strong>Usuario:</strong> {{ detalle.actor }}</p>
        <p class="mb-1"><strong>Fecha:</strong> {{ detalle.fecha }}</p>
        <p class="mb-1"><strong>Acción:</strong> {{ detalle.accionTexto }} en {{ detalle.coleccion }}</p>
        <p class="mb-3"><strong>Documento:</strong> {{ detalle.doc_id || '—' }}</p>

        <b-row>
          <b-col md="6">
            <h6>Antes</h6>
            <pre class="bitacora-json">{{ formatearJson(detalle.antes) }}</pre>
          </b-col>
          <b-col md="6">
            <h6>Después</h6>
            <pre class="bitacora-json">{{ formatearJson(detalle.despues) }}</pre>
          </b-col>
        </b-row>
      </div>
      <div class="d-flex justify-content-end mt-3">
        <b-button variant="secondary" @click="mostrarDetalle = false">Cerrar</b-button>
      </div>
    </b-modal>
  </Layout>
</template>

<style scoped>
.bitacora-json {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 10px;
  font-size: 0.75rem;
  max-height: 320px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
